import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ShieldCheck } from "lucide-react";
import { getConsultant, consultantWa, formatPhone } from "@/data/team";
import { SERVICES, DISCLAIMER } from "@/data/services";
import { FAQS } from "@/data/faqs";
import { Seo, breadcrumbSchema, orgSchema } from "@/components/layout/Seo";
import { Breadcrumbs, MaskLines, Reveal, SectionHead, EASE } from "@/components/ui/motion";
import { FaqList } from "@/components/sections/FaqSection";
import { TeamGrid } from "@/components/sections/TeamGrid";
import { SITE } from "@/lib/config";
import { WaIcon } from "@/components/ui/WaIcon";

const TOPIC = { pinjol: "kewajiban pinjaman online saya", "kartu-kredit": "kewajiban kartu kredit saya", kta: "kewajiban KTA saya" };

const Hero = ({ c }) => (
  <section className="relative overflow-hidden bg-ice" data-testid="consultant-hero">
    <div className="grid-lines absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_70%)]" aria-hidden="true" />
    <div className="wrap relative grid grid-cols-1 items-center gap-12 pb-16 pt-10 sm:pb-24 sm:pt-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Breadcrumbs items={[{ to: "/", label: "Beranda" }, { to: "/konsultan", label: "Konsultan" }, { to: `/konsultan/${c.slug}`, label: c.name }]} />
        <span className="eyebrow mt-10 !flex">{c.role} · Tim Mika Solution</span>
        <MaskLines lines={[c.name]} className="mt-5 font-display text-5xl font-extrabold leading-[0.98] tracking-[-0.035em] sm:text-6xl lg:text-[88px]" />
        <Reveal delay={0.3}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-ink md:text-lg">
            Ceritakan kondisi kewajiban Pinjol, kartu kredit, atau KTA Anda langsung kepada {c.name}. Percakapan bersifat rahasia, dan Anda bebas memutuskan setelah memahami opsi yang tersedia.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href={consultantWa(c)} target="_blank" rel="noopener noreferrer" className="btn btn-sky !py-4 !pl-4 !pr-7" data-testid="consultant-wa-cta">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[#25D366] text-white"><WaIcon size={17} /></span>
              Chat {c.name.split(" ")[0]} via WhatsApp <ArrowUpRight size={16} />
            </a>
            <a href="#konsultasi-topik" className="btn btn-ghost !px-7 !py-4" data-testid="consultant-topics-link">Pilih topik konsultasi</a>
          </div>
          <a href={consultantWa(c)} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-3 text-[15px]" data-testid="consultant-phone">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#25D366]/15 text-[#128C4A]"><WaIcon size={18} /></span>
            <span className="font-display text-xl font-bold tracking-tight text-slateb">{formatPhone(c.wa)}</span>
          </a>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.2 }}
          className="relative mx-auto aspect-[4/5] w-full max-w-[440px] overflow-hidden rounded-t-full rounded-b-[32px] bg-white"
        >
          <img src={c.photo} alt={`Foto ${c.name}, ${c.role}`} className="h-full w-full object-cover object-top" fetchpriority="high" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.9 }} className="relative z-10 mx-auto -mt-12 flex max-w-[360px] items-center gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-[0_30px_60px_-30px_rgba(51,78,104,.45)] backdrop-blur-xl">
          <ShieldCheck className="shrink-0 text-accentb" size={22} />
          <span className="text-[13px] leading-snug text-ink">Bagian dari tim <span className="font-semibold text-slateb">{SITE.parent.name}</span></span>
        </motion.div>
      </div>
    </div>
  </section>
);

export default function ConsultantPage() {
  const { slug } = useParams();
  const c = getConsultant(slug);
  if (!c) return <Navigate to="/konsultan" replace />;
  const crumbs = [{ to: "/", label: "Beranda" }, { to: "/konsultan", label: "Konsultan" }, { to: `/konsultan/${c.slug}`, label: c.name }];
  const person = { "@context": "https://schema.org", "@type": "Person", name: c.name, jobTitle: c.role, image: `${SITE.url}${c.photo}`, worksFor: orgSchema };

  return (
    <>
      <Seo title={`${c.name} — ${c.role} | Mika Solution`} description={`Konsultasikan kewajiban Pinjol, kartu kredit, atau KTA Anda bersama ${c.name}, ${c.role} tim Mika Solution. Hubungi langsung melalui WhatsApp.`} path={`/konsultan/${c.slug}`} type="profile" schema={[person, breadcrumbSchema(crumbs)]} />
      <Hero c={c} />

      <section id="konsultasi-topik" className="py-20 sm:py-28" data-testid="consultant-topics">
        <div className="wrap">
          <SectionHead eyebrow="Topik Konsultasi" title={<>Apa yang ingin Anda <span className="serif-it font-normal text-accentb">bicarakan?</span></>} desc={`Pilih topik, dan pesan WhatsApp ke ${c.name} akan terisi otomatis.`} />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.06}>
                <article className="card group flex h-full flex-col overflow-hidden" data-testid={`consultant-topic-${s.key}`}>
                  <div className="m-3 aspect-[16/10] overflow-hidden rounded-[1.1rem]">
                    <img src={s.image} alt={s.imageAlt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.06]" />
                  </div>
                  <div className="flex flex-1 flex-col p-6 pt-3">
                    <span className="font-display text-sm font-bold text-accentb">{s.index} — {s.short}</span>
                    <h3 className="mt-3 font-display text-xl font-bold leading-tight">{s.name}</h3>
                    <ul className="mt-4 space-y-2">
                      {s.points.slice(0, 3).map((p) => <li key={p} className="flex gap-2 text-[14px] leading-snug text-ink"><Check size={15} className="mt-0.5 shrink-0 text-accentb" strokeWidth={2.5} />{p}</li>)}
                    </ul>
                    <div className="mt-auto flex flex-wrap items-center gap-3 pt-6">
                      <a href={consultantWa(c, TOPIC[s.key])} target="_blank" rel="noopener noreferrer" className="btn btn-sky" data-testid={`consultant-topic-wa-${s.key}`}>Tanya soal {s.short} <ArrowUpRight size={16} /></a>
                      <Link to={`/layanan/${s.slug}`} className="link-u text-sm font-semibold text-slateb">Detail</Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 flex gap-3 rounded-2xl bg-ice p-5 text-[13.5px] leading-relaxed text-ink"><ShieldCheck size={18} className="mt-0.5 shrink-0 text-accentb" />{DISCLAIMER} Kami tidak pernah meminta PIN, OTP, CVV, atau password Anda.</p>
        </div>
      </section>

      <section className="bg-white pb-20 sm:pb-28">
        <div className="wrap grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="Sebelum Chat" title="Yang sering ditanyakan." /></div>
          <div className="lg:col-span-8"><FaqList items={[FAQS[5], FAQS[10], FAQS[7], FAQS[9], FAQS[8]]} /></div>
        </div>
      </section>

      <section className="border-t border-line bg-ice/50 py-20 sm:py-24" data-testid="other-consultants">
        <div className="wrap">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <SectionHead eyebrow="Tim Kami" title="Konsultan lainnya." />
            <Link to="/konsultan" className="link-u text-sm font-semibold text-slateb" data-testid="all-consultants-link">Lihat semua konsultan →</Link>
          </div>
          <TeamGrid exclude={c.slug} limit={6} />
        </div>
      </section>
    </>
  );
}
